import { Button, DropDownMenu, Text, Icon, ClickableItem } from '@upsales/components';
import { getFeatureAvailableFromState, getSoftDeployAccessFromState } from 'App/components/hooks/featureHelper';
import BemClass from '@upsales/components/Utils/bemClass';
import IconName from '@upsales/components/Icon/IconName';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { openEditOrder } from 'App/components/EditOrder';
import { AppState } from 'Store/reducers/AppReducer';
import { useSelector } from 'App/components/hooks';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal/Modal';
import Client from 'App/resources/Model/Client';
import { CardEntity } from './types';
import React from 'react';

/**
 * Whether subscriptions exist for this customer at all.
 *
 * The single source of truth for "does this surface get subscriptions" - the Angular contact card
 * gated its subscription section on exactly these three
 * (`ContactOrderCtrl.hasAgreements`, contactOrder.js), and the create action below has to agree
 * with the section it creates into.
 */
export const hasSubscriptionsAccess = ({ metadata, accountSelf, accessRights }: AppState) =>
	!!metadata?.params.AgreementEnabled &&
	getFeatureAvailableFromState(accountSelf, Feature.RECURRING_ORDER) &&
	accessRights.Agreement;

const createActions = [
	{
		title: 'default.order',
		type: 'order',
		show: ({ accountSelf }: AppState) => {
			return getFeatureAvailableFromState(accountSelf, Feature.ORDERS);
		},
		icon: 'dollar',
		clickAction: (client: Client) => {
			openEditOrder({ type: 'order', clientId: client.id });
		}
	},
	{
		title: 'default.agreement',
		type: 'agreement',
		show: hasSubscriptionsAccess,
		icon: 'recurring-order',
		clickAction: (client: Client, { accountSelf }: AppState) => {
			if (getSoftDeployAccessFromState(accountSelf, 'SUBSCRIPTION_MODAL')) {
				openModal('CreateSubscription', { createdFrom: 'accountSalesAddButton', dontWait: true, client });
			} else {
				Tools.$upModal.open('editAgreement', { clientId: client.id });
			}
		}
	}
] satisfies {
	icon: IconName;
	type: string;
	title: string;
	show?: (appState: AppState) => boolean;
	clickAction: (client: Client, appState: AppState) => void;
}[];

const SalesAddButton = ({ cardEntity }: { cardEntity: CardEntity }) => {
	const classes = new BemClass('SalesAddButton');
	const appData = useSelector(({ App }) => App);
	const client = cardEntity.type === 'client' ? cardEntity.data : (cardEntity.data.client as Client);

	return (
		<DropDownMenu
			className={classes.b()}
			align="right"
			renderTrigger={(expanded, setExpanded) => (
				<Button size="sm" onClick={setExpanded}>
					<Text color="white">
						<Icon name="plus" space="prm" />
						{T('default.add')}
					</Text>
				</Button>
			)}
			noMinWidth
		>
			{close => (
				<>
					{createActions.map(item =>
						item.show === undefined || item.show(appData) ? (
							<ClickableItem
								key={item.type}
								onClick={() => {
									close();
									item.clickAction(client, appData);
								}}
								title={T(item.title)}
								icon={item.icon}
								block
							/>
						) : null
					)}
				</>
			)}
		</DropDownMenu>
	);
};

export default SalesAddButton;
